import React from 'react';
import rest from '../../data/restWrapper';
import dayjs from 'dayjs';
import PermissionCheck from '@components/PermissionCheck';
import translate from '@data/translations';
import useStore from '@data/state/zustand';
import TimePicker from '@components/timePicker';
import { CustomDayPicker } from '@components/DayPicker';

const duplicateInputStyle = {
    border: '2px solid #dc3545 !important',
    boxShadow: '0 0 4px rgba(220, 53, 69, 0.5)',
};
interface Guest {
    cardCodeType: number;
    code: number;
    validToTimestamp: number;
}
const AccessCodes = (props: any) => {
    const remoteDeviceId = props.remoteDeviceId || props.match.params.remoteDeviceId;
    const [accessCodes, setAccessCodes] = React.useState<any>(props.roomSettings.accessCodes);
    const [programAllControllers, setProgramAllControllers] = React.useState(false as boolean);
    const [showBody, setShowBody] = React.useState<boolean>(true);
    const [modified, setModified] = React.useState<any>({
        user1: false,
        user2: false,
        user3: false,
        manager: false,
        security: false,
        maid1: false,
        maid2: false,
        maid3: false,
    });
    const [duplicateCodes, setDuplicateCodes] = React.useState<Record<string, boolean>>({});

    React.useEffect(() => {
        const loadData = async () => {
            if (props.roomSettings.accessCodes) {
                let _accessCodes = { ...props.roomSettings.accessCodes };

                // Postavi vrijeme za kodove koji su 0
                for (const key in _accessCodes) {
                    if (_accessCodes[key] && _accessCodes[key].code === 0) {
                        let checkOutTimeWithAdd2Hours;

                        if (props.reservation) {
                            // checkOut iz rezervacije + 2 sata
                            const checkOutDate = dayjs(props.reservation.checkOutTimestamp);
                            checkOutTimeWithAdd2Hours = checkOutDate.add(2, 'hours').second(0).millisecond(0).valueOf();
                        } else {
                            // checkOutTime iz otherSettings + 2 sata
                            const otherSettings = useStore.getState().otherSettings;
                            const checkOutTime = otherSettings?.checkOutTime || '12:0';
                            const [hours, minutes] = checkOutTime.split(':').map(Number);

                            checkOutTimeWithAdd2Hours = dayjs()
                                .hour(hours)
                                .minute(minutes)
                                .add(2, 'hours')
                                .second(0)
                                .millisecond(0)
                                .valueOf();
                        }

                        _accessCodes[key].validToTimestamp = checkOutTimeWithAdd2Hours;
                    }
                }

                setAccessCodes(_accessCodes);
            }
        };
        loadData().catch((err) => {
            console.log(err);
        });
        // eslint-disable-next-line
    }, [props.roomSettings, props.remoteDeviceId, props.reservation]);

    const getRandomMilisecondsToAddFromRangeOfMinutes = (min: number, max: number) => {
        const otherSettings = useStore.getState().otherSettings;
        let maxInMiliseconds = max * 60 * 1000;
        let randomMilisecondsToAdd = otherSettings?.testModeForSimultaneouslyCardCodeExpiration
            ? 0
            : Math.floor(Math.random() * (maxInMiliseconds - min + 1)) + min;
        return randomMilisecondsToAdd;
    };

    const roundToNearest15 = (date = new Date()) => {
        const minutes = 15;
        const ms = 1000 * 60 * minutes;
        return new Date(Math.floor(date.getTime() / ms) * ms).getTime();
    };

    const onCodeChange = (type: string, e: any) => {
        const value = e.target.value ? parseInt(e.target.value, 10) : 0;
        const _accessCodes = { ...accessCodes };
        const _modified = { ...modified };
        _modified[type] = true;
        _accessCodes[type].code = value;

        const newDuplicateCodes: Record<string, boolean> = {};
        const entries = Object.entries(_accessCodes) as [string, Guest][];

        for (const [keyA, dataA] of entries) {
            newDuplicateCodes[keyA] = entries.some(
                ([keyB, dataB]) => keyA !== keyB && dataA.code !== 0 && dataA.code === dataB.code
            );
        }

        setAccessCodes(_accessCodes);
        setModified(_modified);
        setDuplicateCodes(newDuplicateCodes);
    };

    const handleTimeChange = (type: string, e: any) => {
        let _accessCodes = { ...accessCodes };
        let _modified = { ...modified };
        _modified[type] = true;

        // Uzmi postojeći datum i zamijeni samo vrijeme
        const existingDate = new Date(_accessCodes[type].validToTimestamp);
        const newTime = e.toDate();

        existingDate.setHours(newTime.getHours());
        existingDate.setMinutes(newTime.getMinutes());
        existingDate.setSeconds(0);
        existingDate.setMilliseconds(0);

        _accessCodes[type].validToTimestamp = existingDate.getTime();
        setAccessCodes(_accessCodes);
        setModified(_modified);
    };

    const handleDateChange = (type: string, e: any) => {
        let _accessCodes = { ...accessCodes };
        let _modified = { ...modified };
        _modified[type] = true;

        // Uzmi postojeće vrijeme iz trenutnog timestampa
        const existingDateTime = new Date(_accessCodes[type].validToTimestamp);
        const newDate = new Date(e);

        // Postavi novi datum ali zadrži postojeće vrijeme
        newDate.setHours(existingDateTime.getHours());
        newDate.setMinutes(existingDateTime.getMinutes());
        newDate.setSeconds(existingDateTime.getSeconds());
        newDate.setMilliseconds(existingDateTime.getMilliseconds());

        _accessCodes[type].validToTimestamp = newDate.getTime();
        setAccessCodes(_accessCodes);
        setModified(_modified);
    };

    const handleDateTimeSet = (type: string, targetTimestamp: any) => {
        let _accessCodes = { ...accessCodes };
        let _modified = { ...modified };
        _modified[type] = true;

        _accessCodes[type].validToTimestamp = targetTimestamp;
        setAccessCodes(_accessCodes);
        setModified(_modified);
    };

    const onCardCodeSave = async (cardCodeType: string) => {
        try {
            let cardCodeToSave = accessCodes[cardCodeType];
            let payload = {
                code: cardCodeToSave.code,
                validToTimestamp: cardCodeToSave.validToTimestamp,
                cardCodeType: cardCodeType,
                reservationId: props.reservation ? props.reservation.id : null,
            };

            if (props.onSaveHandler) {
                let accessCodes = props.SRCTemplateData.accessCodes;
                accessCodes[cardCodeType] = cardCodeToSave;
                props.onSaveHandler('accessCodes', accessCodes);
            } else {
                if (props.showOnlyOneSRC || programAllControllers) {
                    if (props.remoteDeviceId1) {
                        await rest('/src/setCardCodeWithValidToTime/' + props.remoteDeviceId1, 'POST', payload);
                    }
                    if (props.remoteDeviceId2) {
                        await rest('/src/setCardCodeWithValidToTime/' + props.remoteDeviceId2, 'POST', payload);
                    }
                    if (props.remoteDeviceId3) {
                        await rest('/src/setCardCodeWithValidToTime/' + props.remoteDeviceId3, 'POST', payload);
                    }
                } else {
                    await rest('/src/setCardCodeWithValidToTime/' + remoteDeviceId, 'POST', payload);
                }
                let _modified = { ...modified };
                _modified[cardCodeType] = false;
                setModified(_modified);
            }
        } catch (err) {
            console.warn(err);
        }
    };

    const getAcceptReservationExpButton = (userType: any) => {
        if (props.reservation) {
            let acceptReservationExpirationButton = null;
            let reservationTime = dayjs(props.reservation.checkOutTimestamp);
            let userTime = dayjs(accessCodes[userType].validToTimestamp);

            if (accessCodes[userType].code !== 0 && Math.abs(userTime.diff(reservationTime, 'hours')) >= 1) {
                acceptReservationExpirationButton = (
                    <button
                        onClick={() => {
                            handleDateTimeSet(userType, dayjs(reservationTime).valueOf());
                        }}
                        className="btn btn-sm btn-warning mt-2 mr-2"
                    >
                        <small>{translate('ACCEPT CURRENT RESERVATION EXP.')}</small>
                    </button>
                );
            }
            return acceptReservationExpirationButton;
        } else {
            return null;
        }
    };

    const discardCode = async (cardCodeType: any) => {
        try {
            let payload = {
                code: 0,
                validToTimestamp: 0,
                cardCodeType,
                reservationId: props.reservation ? Number(props.reservation.id) : null,
            };
            await rest('/src/setCardCodeWithValidToTime/' + remoteDeviceId, 'POST', payload);

            let _accessCodes = { ...accessCodes };
            _accessCodes[cardCodeType].code = 0;
            setAccessCodes(_accessCodes);
        } catch (err) {
            console.log(err);
        }
    };

    const dateFormat = props.otherSettings?.dateFormat ? props.otherSettings.dateFormat : 'DD/MM/YYYY';
    let controlls: any = [];

    for (const key in accessCodes) {
        const value = accessCodes[key];

        const _dateValue = new Date(value.validToTimestamp);
        const _today = new Date();

        const isDateValueToday =
            _dateValue.getFullYear() === _today.getFullYear() &&
            _dateValue.getMonth() === _today.getMonth() &&
            _dateValue.getDate() === _today.getDate();

        const isTodaysTimePassed = isDateValueToday && value.validToTimestamp < Date.now();

        let acceptReservationExpirationButton = getAcceptReservationExpButton(key);

        let template = (
            <div key={key} className="col-md-4 pull-left">
                <div className="mt-2 bg-light p-2 border border-info">
                    <h3 className="text-center">
                        {key === 'manager'
                            ? translate('General access 1')
                            : key === 'security'
                            ? translate('General access 2')
                            : key}
                    </h3>
                    {translate('Access code:')}&ensp;
                    <div className="row mb-1">
                        <div className="col-8">
                            <input
                                onChange={(e) => onCodeChange(key, e)}
                                type="text"
                                className={`input-text form-control form-control-sm ${
                                    duplicateCodes[key] ? 'border border-danger' : ''
                                }`}
                                style={duplicateCodes[key] ? { ...duplicateInputStyle } : {}}
                                value={value.code}
                            />
                        </div>
                        <div className="col-4 text-left">
                            {accessCodes[key].code !== 0 ? (
                                <button onClick={() => discardCode(key)} className="btn btn-sm btn-danger pull-left">
                                    <i className="fa fa-eraser" />
                                </button>
                            ) : null}
                        </div>
                    </div>
                    {translate('Expires')}:&ensp; <small>{dayjs(value.validToTimestamp).format(dateFormat)}</small>
                    <div className="row">
                        <div className="col-8 pr-0" style={{ display: 'flex' }}>
                            <PermissionCheck name="basic_src_update_access_code_settings">
                                <div>
                                    <CustomDayPicker
                                        value={value.validToTimestamp}
                                        onSelect={async (d: any) => {
                                            handleDateChange(key, d);
                                        }}
                                        fromYear={new Date().getFullYear()}
                                        toYear={new Date()?.getFullYear() + 100}
                                        disabledBefore={new Date()}
                                    />
                                </div>
                                <div>
                                    <div
                                        title={translate('Jump to today')}
                                        onClick={() => handleDateChange(key, new Date())}
                                        className="button-primary ml-4p"
                                    >
                                        <i className="fa fa-calendar-times-o"></i>
                                    </div>
                                </div>
                            </PermissionCheck>
                        </div>
                        <div className="col-4 pl-0">
                            <TimePicker
                                value={dayjs(value.validToTimestamp)}
                                onChange={(e) => e && handleTimeChange(key, e)}
                                minuteStep={10}
                            />
                        </div>
                    </div>
                    <hr />
                    <div className="text-center">
                        {duplicateCodes[key] ? (
                            <span className="text-danger">{translate('This card code is already in use')}.</span>
                        ) : null}
                    </div>
                    {!isTodaysTimePassed && (
                        <span>
                            {isDateValueToday
                                ? translate(
                                      'Card code expires today. Make sure to check the expiration time to avoid losing access'
                                  ) + '.'
                                : ''}
                        </span>
                    )}
                    <span>
                        {isTodaysTimePassed
                            ? translate('Card code has expired. Please choose a new expiration time') + '.'
                            : ''}
                    </span>
                    <div className="text-center">
                        {key === 'guest1' || key === 'guest2' || key === 'guest3'
                            ? acceptReservationExpirationButton
                            : null}

                        <button
                            disabled={!modified[key] || duplicateCodes[key]}
                            onClick={() => onCardCodeSave(key)}
                            className="btn btn-sm btn-info mt-2"
                        >
                            {translate('Update')}
                        </button>
                    </div>
                </div>
            </div>
        );

        if (props.template) {
            if (key === 'maid1' || key === 'maid2' || key === 'maid3' || key === 'manager' || key === 'security') {
                controlls.push(template);
            } else {
            }
        } else {
            if (
                key === 'guest1' ||
                key === 'guest2' ||
                key === 'guest3' ||
                key === 'maid1' ||
                key === 'maid2' ||
                key === 'maid3' ||
                key === 'manager' ||
                key === 'security'
            ) {
                controlls.push(template);
            } else {
            }
        }
    }

    return (
        <div className="card mt-2">
            <div
                onClick={() => {
                    setShowBody(!showBody);
                }}
                className="card-header text-light bg-dark text-center pointer"
            >
                {translate('ACCESS CODES')}
            </div>
            {!props.showOnlyOneSRC && props.remoteDeviceId1 && (props.remoteDeviceId2 || props.remoteDeviceId3) && (
                <div className="px-3 pt-2">
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="programAllControllers"
                            checked={programAllControllers}
                            onChange={(e) => setProgramAllControllers(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="programAllControllers">
                            {translate('Apply to all controllers')}
                        </label>
                    </div>
                </div>
            )}

            <div className="clear mb-2" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                {controlls}
            </div>
        </div>
    );
};

export default AccessCodes;
